import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["content", "mediaInput", "preview", "previewGrid", "counter", "submitButton", "form", "thumbnailInput"]

  connect() {
    this.mediaFiles = []
    this.videoThumbnails = {}
    this.uploadedInputs = new Map()
    this.activeUploads = 0
    this.updateCharacterCount()

    if (this.hasFormTarget) {
      this.formTarget.addEventListener('turbo:submit-end', (e) => this.handleSubmitEnd(e))
    }
  }

  handleMediaUpload(event) {
    console.log('handleMediaUpload called', this.mediaFiles)
    
    if (!this.mediaFiles) {
      this.mediaFiles = []
    }
    
    const files = Array.from(event.target.files)
    console.log('Files selected:', files.length)
    
    files.forEach(file => {
      if (this.mediaFiles.length >= 4) {
        alert("You can only upload up to 4 media files per post")
        return
      }

      console.log('Adding file:', file.name, file.type)
      this.mediaFiles.push(file)
      this.createMediaPreview(file)
      this.startDirectUpload(file)
    })

    if (this.mediaFiles.length > 0) {
      this.showPreviewArea()
    }

    // Clear input so user can pick more files while uploads continue in background
    event.target.value = ''

    this.updateCharacterCount()
  }

  createMediaPreview(file) {
    const previewElement = document.createElement('div')
    previewElement.className = 'relative group rounded-lg overflow-hidden bg-gray-100'
    
    // Remove button
    const removeButton = document.createElement('button')
    removeButton.type = 'button'
    removeButton.className = 'absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10'
    removeButton.innerHTML = '<i class="fas fa-times text-xs"></i>'
    removeButton.addEventListener('click', () => this.removeMedia(file, previewElement))
    
    if (file.type.startsWith('image/')) {
      const img = document.createElement('img')
      img.className = 'w-full max-h-64 object-contain rounded-lg'
      img.src = URL.createObjectURL(file)
      previewElement.appendChild(img)
    } else if (file.type.startsWith('video/')) {
      const video = document.createElement('video')
      video.className = 'w-full max-h-64 object-cover rounded-lg'
      video.src = URL.createObjectURL(file)
      video.muted = true
      video.preload = 'metadata'
      
      // Create canvas to capture first frame
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      // Create loading state while video loads
      const loadingDiv = document.createElement('div')
      loadingDiv.className = 'w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center'
      loadingDiv.innerHTML = '<i class="fas fa-video text-gray-400 text-2xl"></i>'
      previewElement.appendChild(loadingDiv)
      
      let thumbnailAttempts = 0
      const maxAttempts = 5
      const seekTimes = [2, 5, 10, 15, 30] // Try these seconds into the video
      
      video.addEventListener('loadedmetadata', () => {
        console.log('Video loaded:', video.videoWidth, 'x', video.videoHeight, 'duration:', video.duration)
        // Set canvas size to match video
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        
        // Start with the first seek time
        this.tryNextThumbnail(video, canvas, ctx, seekTimes, thumbnailAttempts, maxAttempts, loadingDiv, previewElement, file)
      })
      
      
      video.addEventListener('error', () => {
        loadingDiv.innerHTML = '<i class="fas fa-exclamation-triangle text-red-400 text-2xl"></i><div class="text-xs text-red-500 mt-2">Failed to load video</div>'
      })
    }
    
    previewElement.appendChild(removeButton)
    this.previewGridTarget.appendChild(previewElement)
  }

  removeMedia(file, previewElement) {
    const index = this.mediaFiles.indexOf(file)
    if (index > -1) this.mediaFiles.splice(index, 1)

    const hiddenInput = this.uploadedInputs.get(file)
    if (hiddenInput) {
      hiddenInput.remove()
      this.uploadedInputs.delete(file)
    }

    previewElement.remove()

    if (this.mediaFiles.length === 0) {
      this.previewTarget.classList.add('hidden')
    }

    this.updateCharacterCount()
  }

  showPreviewArea() {
    this.previewTarget.classList.remove('hidden')
  }

  updateCharacterCount() {
    if (!this.hasContentTarget || !this.hasCounterTarget || !this.hasSubmitButtonTarget) return
    
    if (!this.mediaFiles) {
      this.mediaFiles = []
    }
    
    const length = this.contentTarget.value.length
    this.counterTarget.textContent = `${length}/280`
    
    // Update submit button state
    const isEmpty = length === 0 && this.mediaFiles.length === 0
    const tooLong = length > 280
    const uploading = this.activeUploads > 0

    this.submitButtonTarget.disabled = isEmpty || tooLong || uploading
    
    // Update counter color/text
    if (uploading) {
      this.counterTarget.textContent = `Uploading ${this.activeUploads}...`
      this.counterTarget.className = 'text-blue-400 text-sm'
    } else if (tooLong) {
      this.counterTarget.className = 'text-red-500 text-sm font-medium'
    } else if (length > 240) {
      this.counterTarget.className = 'text-orange-500 text-sm'
    } else {
      this.counterTarget.className = 'text-gray-400 text-sm'
    }
  }

  // Auto-expand textarea as user types
  contentTargetConnected() {
    this.contentTarget.addEventListener('input', () => {
      this.updateCharacterCount()
      this.autoResize()
    })
    
    // Publish deliberately with Command/Ctrl + Enter; plain Enter stays available
    // for writing the kind of useful, multi-line posts this community needs.
    this.contentTarget.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        this.submitPost()
      }
    })
    
    // Initial auto-resize
    this.autoResize()
  }
  
  submitPost() {
    if (this.submitButtonTarget.disabled) return
    
    // Let Rails 8 Turbo handle the submission
    this.formTarget.requestSubmit()
  }

  autoResize() {
    if (!this.hasContentTarget) return
    
    const textarea = this.contentTarget
    // Save current scroll position
    const scrollTop = textarea.scrollTop
    
    // Reset height to auto to get the natural height
    textarea.style.height = 'auto'
    
    // Set the height based on scroll height, with min/max constraints
    const minHeight = 38  // Match the min_height from the helper
    const maxHeight = 120 // Match the max_height from the helper
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight)
    
    textarea.style.height = `${newHeight}px`
    
    // Restore scroll position if content was scrollable
    if (newHeight === maxHeight) {
      textarea.scrollTop = scrollTop
    }
  }
  
  // Make sure auto-resize is called when the form is reset/replaced
  formTargetConnected() {
    // Re-initialize after Turbo replacements
    requestAnimationFrame(() => {
      this.autoResize()
      this.updateCharacterCount()
    })
  }

  startDirectUpload(file) {
    if (!this.hasMediaInputTarget) return

    this.activeUploads += 1
    this.updateCharacterCount()

    const upload = new DirectUpload(file, this.mediaInputTarget.dataset.directUploadUrl)
    upload.create((error, blob) => {
      this.activeUploads = Math.max(0, this.activeUploads - 1)

      if (error) {
        console.error('Direct upload failed:', error)
        this.showUploadError()
      } else {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = this.mediaInputTarget.name
        input.value = blob.signed_id
        input.dataset.directUploadId = blob.id
        this.formTarget.appendChild(input)
        this.uploadedInputs.set(file, input)
      }

      this.updateCharacterCount()
    })
  }

  handleSubmitEnd(event) {
    if (event.detail.success) {
      this.resetComposer()
    }
  }

  resetComposer() {
    if (this.hasContentTarget) {
      this.contentTarget.value = ''
      this.contentTarget.style.height = 'auto'
      this.autoResize()
    }

    this.mediaFiles = []
    this.videoThumbnails = {}

    if (this.hasPreviewGridTarget) this.previewGridTarget.innerHTML = ''
    if (this.hasPreviewTarget) this.previewTarget.classList.add('hidden')

    this.formTarget.querySelectorAll('input[type="hidden"][name$="[media][]"]').forEach(el => el.remove())
    this.uploadedInputs = new Map()

    if (this.hasThumbnailInputTarget) this.thumbnailInputTarget.value = ''

    this.updateCharacterCount()
  }

  showUploadError() {
    const toast = document.createElement('div')
    toast.className = 'fixed top-4 right-4 px-4 py-2 rounded-lg bg-red-500 text-white text-sm z-[10000]'
    toast.textContent = 'Upload failed for one file. Please try again.'
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 2500)
  }

  disconnect() {
    // Rails 8 Turbo handles cleanup automatically
  }
  
  tryNextThumbnail(video, canvas, ctx, seekTimes, attemptIndex, maxAttempts, loadingDiv, previewElement, file) {
    if (attemptIndex >= maxAttempts || attemptIndex >= seekTimes.length) {
      // Give up and show file info
      console.log('Could not generate thumbnail after', attemptIndex, 'attempts')
      loadingDiv.innerHTML = `
        <div class="text-center">
          <i class="fas fa-video text-gray-500 text-3xl mb-2"></i>
          <div class="text-sm text-gray-600">${file.name}</div>
          <div class="text-xs text-gray-500">${this.formatFileSize(file.size)}</div>
        </div>
      `
      return
    }
    
    const seekTime = Math.min(seekTimes[attemptIndex], video.duration - 1)
    console.log(`Attempting thumbnail ${attemptIndex + 1}/${maxAttempts} at ${seekTime}s`)
    
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked)
      
      try {
        // Draw video frame to canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        
        // Get image data to check if it's mostly black
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const brightness = this.calculateBrightness(imageData.data)
        
        console.log(`Frame at ${seekTime}s has brightness: ${brightness}`)
        
        // If brightness is too low (likely black frame), try next time
        if (brightness < 30 && attemptIndex < maxAttempts - 1) {
          console.log('Frame too dark, trying next position...')
          this.tryNextThumbnail(video, canvas, ctx, seekTimes, attemptIndex + 1, maxAttempts, loadingDiv, previewElement, file)
          return
        }
        
        // This frame looks good enough, use it
        const thumbnailImg = document.createElement('img')
        thumbnailImg.className = 'w-full object-contain rounded-lg'
        thumbnailImg.style.maxHeight = '300px'
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
        thumbnailImg.src = dataUrl
        
        console.log('Thumbnail generated successfully, data URL length:', dataUrl.length)
        
        // Store thumbnail data for this video file
        this.videoThumbnails[file.name] = dataUrl
        
        // Replace loading div with thumbnail
        loadingDiv.remove()
        previewElement.appendChild(thumbnailImg)
        
        // Add video indicator overlay
        const videoOverlay = document.createElement('div')
        videoOverlay.className = 'absolute top-2 left-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs pointer-events-none'
        videoOverlay.innerHTML = '<i class="fas fa-video mr-1"></i>Video'
        previewElement.appendChild(videoOverlay)
        
      } catch (error) {
        console.error('Error generating thumbnail:', error)
        // Try next position
        if (attemptIndex < maxAttempts - 1) {
          this.tryNextThumbnail(video, canvas, ctx, seekTimes, attemptIndex + 1, maxAttempts, loadingDiv, previewElement, file)
        } else {
          // Final fallback
          loadingDiv.innerHTML = `
            <div class="text-center">
              <i class="fas fa-video text-gray-500 text-3xl mb-2"></i>
              <div class="text-sm text-gray-600">${file.name}</div>
              <div class="text-xs text-gray-500">${this.formatFileSize(file.size)}</div>
            </div>
          `
        }
      }
    }
    
    video.addEventListener('seeked', onSeeked)
    video.currentTime = seekTime
  }
  
  calculateBrightness(imageData) {
    let totalBrightness = 0
    const pixelCount = imageData.length / 4
    
    // Sample every 100th pixel for performance
    for (let i = 0; i < imageData.length; i += 400) { // 400 = 4 * 100
      const r = imageData[i]
      const g = imageData[i + 1]
      const b = imageData[i + 2]
      // Calculate luminance using standard formula
      totalBrightness += (r * 0.299 + g * 0.587 + b * 0.114)
    }
    
    return totalBrightness / (pixelCount / 100)
  }
  
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }
}
